import React, { useState } from "react";

interface RegisterEntry {
  id: string;
  data: Record<string, string>;
  updatedAt: string;
}

interface McaCheckItemRecord {
  id: string;
  label: string;
  status: string;
  note: string;
}

interface McaCheckRecord {
  id: string;
  period: string;
  completedAt: string;
  completedBy: string;
  items: McaCheckItemRecord[];
  failed: number;
}

const REGISTER_KEY = "mca_register";
const CHECK_KEY = "mca_check_records";

interface FieldDef {
  key: string;
  label: string;
  kind: "text" | "date" | "textarea" | "select";
  options?: string[];
}

const rf = (key: string, label: string, kind: "text" | "date" | "textarea" | "select" = "text", options?: string[]): FieldDef => ({ key, label, kind, options });

const REGISTER_FIELDS: FieldDef[] = [
  rf("residentName", "Resident name"),
  rf("room", "Room"),
  rf("legalBasis", "Legal basis", "select", ["Capacity with support", "Best interest decision", "DoLS authorisation", "Standard authorisation", "Court of Protection order", "Deputyship", "No restriction"]),
  rf("authorisationRef", "Authorisation or reference number"),
  rf("startDate", "Start date", "date"),
  rf("endDate", "End or review date", "date"),
  rf("conditions", "Conditions attached to the authorisation", "textarea"),
  rf("restrictions", "Restrictions in place, rationale and least restrictive review", "textarea"),
  rf("imca", "IMCA or advocate involved, name and contact"),
  rf("documents", "Documents on file: capacity assessments, best interest checklist, consultation records", "textarea"),
];

interface CheckDef {
  id: string;
  name: string;
  period: "daily" | "weekly" | "monthly";
  items: { id: string; label: string }[];
}

const MCA_CHECKS: CheckDef[] = [
  { id: "mca-daily", name: "Daily MCA Check", period: "daily", items: [
    { id: "md-1", label: "Care delivered in line with any authorisation conditions" },
    { id: "md-2", label: "Any restriction used today is recorded, justified and proportionate" },
    { id: "md-3", label: "Resident wishes and any objections observed and recorded" },
    { id: "md-4", label: "New signs of deprivation of liberty reported for assessment" },
    { id: "md-5", label: "Restraint or restriction records from the last shift reviewed and signed" },
    { id: "md-6", label: "Least restrictive options offered and recorded for each resident on the register" },
  ]},
  { id: "mca-weekly", name: "Weekly MCA Review", period: "weekly", items: [
    { id: "mw-1", label: "Authorisation conditions compliance reviewed for every resident on the register" },
    { id: "mw-2", label: "Restrictions reviewed against the least restrictive principle" },
    { id: "mw-3", label: "Mood, behaviour and objection trends reviewed" },
    { id: "mw-4", label: "IMCA or advocate contact logged and next contact planned" },
    { id: "mw-5", label: "Care plans reflect current MCA status and conditions" },
    { id: "mw-6", label: "Family and representatives involvement recorded" },
  ]},
  { id: "mca-monthly", name: "Monthly MCA Audit", period: "monthly", items: [
    { id: "mm-1", label: "Register accuracy reviewed against current residents" },
    { id: "mm-2", label: "Authorisations expiring within 60 and 30 days flagged with referral started" },
    { id: "mm-3", label: "All review dates met with no overdue reviews" },
    { id: "mm-4", label: "Capacity assessments repeated after any change in condition" },
    { id: "mm-5", label: "Best interest decisions include consultation evidence" },
    { id: "mm-6", label: "Staff MCA knowledge spot checked with at least two staff" },
    { id: "mm-7", label: "Full documentation audit: assessments, checklists, conditions, reviews" },
    { id: "mm-8", label: "Learning from any restriction or restraint event shared with the team" },
  ]},
];

function loadRegister(): RegisterEntry[] {
  try {
    return JSON.parse(localStorage.getItem(REGISTER_KEY) || "[]");
  } catch (error) {
    return [];
  }
}

function loadChecks(): McaCheckRecord[] {
  try {
    return JSON.parse(localStorage.getItem(CHECK_KEY) || "[]");
  } catch (error) {
    return [];
  }
}
export default function Dols({ user }: { user?: any }) {
  const [tab, setTab] = useState<"register" | "checks" | "history">("register");
  const [register, setRegister] = useState<RegisterEntry[]>(loadRegister);
  const [form, setForm] = useState<Record<string, string>>({});
  const [editingId, setEditingId] = useState("");

  const [checkId, setCheckId] = useState("mca-daily");
  const [checkItems, setCheckItems] = useState<McaCheckItemRecord[]>(() => MCA_CHECKS[0].items.map(item => ({ id: item.id, label: item.label, status: "", note: "" })));
  const [checks, setChecks] = useState<McaCheckRecord[]>(loadChecks);
  const [message, setMessage] = useState("");

  const checkDef = MCA_CHECKS.find(c => c.id === checkId) || MCA_CHECKS[0];

  const setField = (key: string, value: string) => setForm(current => ({ ...current, [key]: value }));

  const saveEntry = () => {
    if (!form.residentName) {
      setMessage("Resident name is required.");
      return;
    }

    const entry: RegisterEntry = { id: editingId || `mca-${Date.now()}`, data: form, updatedAt: new Date().toISOString() };
    const rest = register.filter(item => item.id !== entry.id);
    const next = [entry, ...rest];

    setRegister(next);
    localStorage.setItem(REGISTER_KEY, JSON.stringify(next));
    setMessage(`Register entry for ${form.residentName} saved.`);
    setForm({});
    setEditingId("");
  };

  const editEntry = (entry: RegisterEntry) => {
    setForm(entry.data);
    setEditingId(entry.id);
  };

  const deleteEntry = (id: string) => {
    const next = register.filter(item => item.id !== id);

    setRegister(next);
    localStorage.setItem(REGISTER_KEY, JSON.stringify(next));
  };

  const switchCheck = (id: string) => {
    const next = MCA_CHECKS.find(c => c.id === id);

    if (!next) return;

    setCheckId(id);
    setCheckItems(next.items.map(item => ({ id: item.id, label: item.label, status: "", note: "" })));
    setMessage("");
  };

  const updateCheckItem = (id: string, patch: Partial<McaCheckItemRecord>) => {
    setCheckItems(current => current.map(item => (item.id === id ? { ...item, ...patch } : item)));
  };

  const saveCheck = () => {
    const unchecked = checkItems.filter(item => !item.status).length;

    if (unchecked > 0) {
      setMessage(`${unchecked} items still unanswered.`);
      return;
    }

    const failed = checkItems.filter(item => item.status === "fail").length;

    const record: McaCheckRecord = {
      id: `mcacheck-${Date.now()}`,
      period: checkDef.name,
      completedAt: new Date().toISOString(),
      completedBy: user && user.name ? user.name : "Unknown",
      items: checkItems,
      failed,
    };

    const next = [record, ...checks];

    setChecks(next);
    localStorage.setItem(CHECK_KEY, JSON.stringify(next));
    setMessage(`${checkDef.name} saved with ${failed} failed items. Failures appear in Action Plans.`);
    setCheckItems(checkDef.items.map(item => ({ id: item.id, label: item.label, status: "", note: "" })));
  };

  const daysTo = (value: string) => {
    if (!value) return null;

    const ms = new Date(value).getTime() - Date.now();

    return Math.ceil(ms / 86400000);
  };
  const renderField = (field: FieldDef) => {
    const value = form[field.key] || "";

    if (field.kind === "textarea") {
      return <textarea value={value} onChange={event => setField(field.key, event.target.value)} rows={2} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />;
    }

    if (field.kind === "select") {
      return (
        <select value={value} onChange={event => setField(field.key, event.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm">
          <option value=""></option>
          {(field.options || []).map(option => <option key={option}>{option}</option>)}
        </select>
      );
    }

    return <input type={field.kind === "date" ? "date" : "text"} value={value} onChange={event => setField(field.key, event.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />;
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">MCA / DoLS</h2>
        <p className="text-sm text-slate-600">Register of residents with capacity, best interest or authorisation arrangements, plus daily, weekly and monthly compliance checks with automatic action plans.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setTab("register")} className={tab === "register" ? "rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"}>Register</button>
        <button onClick={() => setTab("checks")} className={tab === "checks" ? "rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"}>Checks</button>
        <button onClick={() => setTab("history")} className={tab === "history" ? "rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"}>History</button>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      {tab === "register" ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 font-semibold text-slate-900">{editingId ? "Edit entry" : "Add resident to register"}</h3>
            <div className="grid gap-3 md:grid-cols-2">
              {REGISTER_FIELDS.map(field => (
                <div key={field.key} className={field.kind === "textarea" ? "md:col-span-2" : ""}>
                  <label className="mb-1 block text-xs font-medium text-slate-600">{field.label}</label>
                  {renderField(field)}
                </div>
              ))}
            </div>
            <button onClick={saveEntry} className="mt-3 rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Save register entry</button>
          </div>

          <div className="space-y-3">
            {register.map(entry => {
              const days = daysTo(entry.data.endDate || "");

              return (
                <div key={entry.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-medium text-slate-900">{entry.data.residentName} <span className="text-xs text-slate-500">Room {entry.data.room || "-"}</span></div>
                    <div className="flex items-center gap-2">
                      {days !== null && days < 0 ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">OVERDUE REVIEW</span> : null}
                      {days !== null && days >= 0 && days <= 60 ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">Review in {days} days</span> : null}
                      <button onClick={() => editEntry(entry)} className="rounded-lg border border-slate-300 px-3 py-1 text-sm">Edit</button>
                      <button onClick={() => deleteEntry(entry.id)} className="rounded-lg border border-red-300 px-3 py-1 text-sm text-red-700">Remove</button>
                    </div>
                  </div>
                  <div className="mt-1 text-xs text-slate-500">{entry.data.legalBasis || "No legal basis set"} | Ref {entry.data.authorisationRef || "-"} | Ends {entry.data.endDate || "-"}</div>
                </div>
              );
            })}
            {register.length === 0 ? <div className="text-sm text-slate-500">No residents on the register yet.</div> : null}
          </div>
        </div>
      ) : null}
      {tab === "checks" ? (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {MCA_CHECKS.map(c => (
              <button key={c.id} onClick={() => switchCheck(c.id)} className={c.id === checkId ? "rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"}>
                {c.name}
              </button>
            ))}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="space-y-4">
              {checkItems.map(item => (
                <div key={item.id} className="rounded-lg border border-slate-100 p-3">
                  <div className="text-sm font-medium text-slate-800">{item.label}</div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <button onClick={() => updateCheckItem(item.id, { status: "pass" })} className={item.status === "pass" ? "rounded-lg bg-green-700 px-3 py-1 text-sm text-white" : "rounded-lg border border-slate-300 px-3 py-1 text-sm text-slate-700"}>Pass</button>
                    <button onClick={() => updateCheckItem(item.id, { status: "fail" })} className={item.status === "fail" ? "rounded-lg bg-red-700 px-3 py-1 text-sm text-white" : "rounded-lg border border-slate-300 px-3 py-1 text-sm text-slate-700"}>Fail</button>
                    <input value={item.note} onChange={event => updateCheckItem(item.id, { note: event.target.value })} placeholder="Note or action" className="flex-1 rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button onClick={saveCheck} className="rounded-lg bg-blue-900 px-5 py-2 text-sm font-medium text-white">Save check</button>
        </div>
      ) : null}

      {tab === "history" ? (
        <div className="space-y-3">
          {checks.map(record => (
            <div key={record.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-medium text-slate-900">{record.period}</div>
                <div className="text-sm text-slate-600">Failed {record.failed}</div>
              </div>
              <div className="mt-1 text-xs text-slate-500">{new Date(record.completedAt).toLocaleString()} by {record.completedBy}</div>
              {record.items.filter(item => item.status === "fail").map(item => (
                <div key={item.id} className="mt-1 text-xs text-red-700">FAIL: {item.label}{item.note ? ` - ${item.note}` : ""}</div>
              ))}
            </div>
          ))}
          {checks.length === 0 ? <div className="text-sm text-slate-500">No checks recorded yet.</div> : null}
        </div>
      ) : null}
    </div>
  );
}
